import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { useEffect, useState } from 'react'
import Skeleton from 'react-loading-skeleton'
import styled from 'styled-components'
import { SITE_PATH } from '../../constants/common'
import { CustomLink } from '../../elements/CustomLink'
import { useAppSelector } from '../../types/assets'

export const StyledStripeFormContainer = styled.div`
  .submit-button {
    color: ${props => props.theme.buttonColor};
    font-size: ${({ large }: { large?: boolean }) => (large ? '13pt' : '11pt')};
    background: ${props => props.theme.buttonFill};
    border-radius: 2px;
    padding: ${({ large }: { large?: boolean; disabled?: boolean }) => (large ? '14px 30px' : '10px 20px')};
    border: 0;
    text-decoration: none;
    cursor: pointer;
    filter: ${props => props.disabled && 'grayscale(75%)'};
    width: 200px;
    margin-top: 2.5rem;
  }
  .sample-cards {
    color: rgb(192, 192, 192);
    margin-top: 2rem;
  }
  .fa {
    margin-right: 8px;
    margin-left: -10px;
  }
  .card-form {
    margin-top: 1rem;
  }
  button:hover {
    background: ${props => !props.disabled && props.theme.highlightColor};
    animation: background 0.2s ease-in;
  }

  .message {
    margin-bottom: 0.5em;
    color: red;
    display: block;
    a {
      color: ${props => props.theme.fontColor};
      cursor: pointer;
      text-decoration: underline;
    }
    a:hover {
      color: ${props => props.theme.highlightColor};
    }
  }
`

export const StripePayment = (props: any) => {
  const msg: any = null
  const [message, setMessage] = useState(msg)
  const [, setIsLoading] = useState(true)
  const [isLoadingElement, setIsLoadingElement] = useState(true)
  const [isProcessing, setIsProcessing] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [alreadyPaid, setAlreadyPaid] = useState(false)
  const { clientSecret, isPaymentDemoMode, setPaid, intakeName } = props

  const stripe = useStripe()
  const elements = useElements()
  const { path } = useAppSelector(state => state.settings.config)

  const handleSubmit = async (e: any) => {
    e.preventDefault()

    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return
    }

    setIsProcessing(true)

    const currentHost = (): string => {
      const urlParts = window.location.href.split('/')
      return urlParts[0] + '//' + urlParts[2]
    }

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${currentHost()}/${SITE_PATH}/application-submitted?intake-name=${intakeName}`,
      },
    })
    if (error?.message) {
      setMessage(error.message)
    }
    setIsProcessing(false)
    setSubmitted(true)
  }

  useEffect((): any => {
    if (!stripe) {
      return
    }

    if (!clientSecret) {
      return
    }

    let mounted = true
    stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent }) => {
      switch (paymentIntent?.status) {
        case 'succeeded':
          setMessage('Payment succeeded!')
          setAlreadyPaid(true)
          if (mounted) setPaid(true)
          break
        case 'processing':
          setMessage('Your payment is processing.')
          break
        case 'requires_payment_method':
          setMessage(' Your payment was not successful, please try again.')
          break
        default:
          setMessage('Something went wrong.')
          break
      }
      setIsLoading(false)
    })
    return () => (mounted = false)
  }, [clientSecret, stripe, setPaid])

  if (!submitted && alreadyPaid) {
    return (
      <StyledStripeFormContainer>
        <h3>The application is already paid.</h3>
        <p></p>
        <CustomLink
          to={`/${path}/courses}`}
          label="Back to website to look at more courses"
        />
      </StyledStripeFormContainer>
    )
  }
  //setPaidStatus(true);
  return (
    <StyledStripeFormContainer>
      {isPaymentDemoMode && (
        <div className="message">
          Payments is in DEMO mode. Do not use real card information. See{' '}
          <a
            onClick={() => {
              const url = 'https://stripe.com/docs/testing'
              const newWin = window.open(url, '_blank')
              if (!newWin || newWin.closed || typeof newWin.closed == 'undefined') {
                window.location.href = url
              }
            }}
          >
            testing cards
          </a>
          .
        </div>
      )}

      <span className="message">{submitted && message && <p>{message}</p>}</span>
      <PaymentElement
        className="card-form"
        id="payment-element"
        onReady={() => setIsLoadingElement(false)}
      />
      {isLoadingElement && (
        <div>
          <p>
            <Skeleton
              width="100%"
              height="30px"
            />
          </p>
          <p>
            <Skeleton
              width="100%"
              height="30px"
            />
          </p>
          <p>
            <Skeleton
              width="100%"
              height="30px"
            />
          </p>
          <p>
            <Skeleton
              width="100%"
              height="30px"
            />
          </p>
        </div>
      )}
      <div>
        {isPaymentDemoMode && (
          <div className="sample-cards">
            Card will succeed: 4242 4242 4242 4242 <br />
            Card needs auth: 4000 0025 0000 3155 <br />
            Card will be declined: 4000 0000 0000 9995
          </div>
        )}
        <button
          className="submit-button"
          disabled={isProcessing || !stripe || !elements}
          id="submit"
          onClick={handleSubmit}
        >
          {isProcessing && <i className="fa fa-spinner fa-spin fa-fw"></i>} <span>SUBMIT PAYMENT</span>
        </button>
      </div>
    </StyledStripeFormContainer>
  )
}
